import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import { Printer, RefreshCw, AlertTriangle, CheckCircle2, Tag, Undo2, Loader2, X } from 'lucide-react';
import { ensureOrderCoords } from './geocode';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isOwnShipping } from './shipping';
import { CABIFY_DEAD, CABIFY_STATE_ES } from './CabifyBatch';
import { orderDisplayId } from './OrderPeek';
import { logActivity } from './logger';
import { cn } from './ui/Table';
import { Thumb } from './ui/Thumb';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Checkbox } from './ui/arc/checkbox';
import { MetricCard } from './ui/arc/metric-card';
import { EmptyState } from './ui/arc/empty-state';
import SegmentedControl from './ui/arc/segmented-control';

// Control de despacho Cabify (27/10/2026). Antes de que Cabify retire los paquetes se revisa,
// pedido por pedido, que esté todo: productos y la cantidad de ETIQUETAS (un Sun&Moon va en 2
// bultos → 2 etiquetas; el 22/10 salieron con 1). Solo pedidos EMPAQUETADOS que van por Cabify.
// El tilde es compartido (orders.dispatch_checked_at/by, migr 116) y la lista se imprime en PDF
// para chequear en papel.

interface DOrder {
  id: number; tiendanube_id: string | null; source: string | null; status: string;
  contact_name: string | null; products: any[] | null; delivery_type: string | null;
  shipping_option: string | null; delivery_address: string | null; delivery_unit: string | null;
  dispatch_checked_at: string | null; dispatch_checked_by: string | null; created_at: string;
  contact_phone: string | null; canje_phone: string | null; delivery_lat: number | null; delivery_lon: number | null;
}
interface Label { order_id: number; external_id: string | null; status: string; parcel_id: string; }

const COLS = 'id, tiendanube_id, source, status, contact_name, products, delivery_type, shipping_option, delivery_address, delivery_unit, dispatch_checked_at, dispatch_checked_by, created_at, contact_phone, canje_phone, delivery_lat, delivery_lon';
const norm = (s: string) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/\[[^\]]*\]/g, ' ').replace(/[^a-z0-9&]+/g, ' ').trim();
const shortAddr = (o: DOrder) => [String(o.delivery_address || '').split(',').slice(0, 2).join(','), o.delivery_unit].filter(Boolean).join(' · ');
const itemName = (it: any) => String(it?.name || '').replace(/\[[^\]]*\]\s*/g, '').trim();

export function ControlDespacho({ userEmail }: { userEmail?: string }) {
  const [orders, setOrders] = useState<DOrder[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [multi, setMulti] = useState<{ id: number; name: string; bultos: number }[]>([]);
  const [varToProd, setVarToProd] = useState<Map<number, number>>(new Map());
  const [images, setImages] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'pendientes' | 'todos' | 'chequeados'>('pendientes');
  const [saving, setSaving] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | 'all' | null>(null);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('orders').select(COLS).eq('status', 'empaquetado').is('deleted_at', null).order('created_at');
    const list = ((data || []) as DOrder[]).filter(o => isOwnShipping(o));
    const ids = list.map(o => o.id);
    const items = list.flatMap(o => Array.isArray(o.products) ? o.products : []);
    const vIds = Array.from(new Set(items.map(it => Number(it.tn_variant_id)).filter(Boolean)));
    const [lab, mul, vs] = await Promise.all([
      ids.length ? supabase.from('cabify_deliveries').select('order_id, external_id, status, parcel_id').in('order_id', ids) : Promise.resolve({ data: [] as any[] }),
      supabase.from('tn_products').select('id, name, cabify_bultos').gt('cabify_bultos', 1),
      vIds.length ? supabase.from('tn_variants').select('id, product_id').in('id', vIds) : Promise.resolve({ data: [] as any[] }),
    ]);
    const v2p = new Map<number, number>(((vs.data || []) as any[]).map(v => [Number(v.id), Number(v.product_id)]));
    const pIds = Array.from(new Set(items.map(it => Number(it.tn_product_id) || v2p.get(Number(it.tn_variant_id)) || 0).filter(Boolean)));
    const imgs = pIds.length ? await supabase.from('tn_products').select('id, image_url').in('id', pIds) : { data: [] as any[] };
    setOrders(list);
    setLabels(((lab.data || []) as Label[]).filter(l => !CABIFY_DEAD.includes(l.status)));
    setMulti(((mul.data || []) as any[]).map(p => ({ id: Number(p.id), name: p.name, bultos: Number(p.cabify_bultos) || 1 })));
    setVarToProd(v2p);
    setImages(new Map(((imgs.data || []) as any[]).filter(p => p.image_url).map(p => [Number(p.id), p.image_url])));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const pidOf = useCallback((it: any) => Number(it?.tn_product_id) || varToProd.get(Number(it?.tn_variant_id)) || 0, [varToProd]);

  // Bultos esperados: el mayor cabify_bultos de los ítems (misma regla que cabify-logistics).
  const expectedBultos = useCallback((o: DOrder) => {
    let max = 1;
    for (const it of (o.products || [])) {
      const pid = pidOf(it);
      const byId = multi.find(p => p.id === pid);
      if (byId) { max = Math.max(max, byId.bultos); continue; }
      if (pid) continue;
      const base = norm(String(it?.name || '').split(' (')[0].split(' – ')[0]);
      for (const p of multi) { const pn = norm(p.name); if (pn && (pn === base || pn.includes(base) || base.includes(pn))) max = Math.max(max, p.bultos); }
    }
    return max;
  }, [multi, pidOf]);

  const rows = useMemo(() => orders.map(o => {
    const ls = labels.filter(l => l.order_id === o.id).sort((a, b) => String(a.external_id).localeCompare(String(b.external_id)));
    const expected = expectedBultos(o);
    const problem = ls.length === 0 ? 'Sin etiqueta Cabify' : ls.length < expected ? `Faltan ${expected - ls.length} etiqueta${expected - ls.length > 1 ? 's' : ''} (van ${expected} bultos)` : null;
    return { o, ls, expected, problem, checked: !!o.dispatch_checked_at };
  }), [orders, labels, expectedBultos]);

  const shown = rows.filter(r => view === 'todos' || (view === 'chequeados' ? r.checked : !r.checked));
  const nLabels = rows.reduce((s, r) => s + r.ls.length, 0);
  const nChecked = rows.filter(r => r.checked).length;
  const nProblems = rows.filter(r => r.problem).length;

  const toggle = async (o: DOrder, on: boolean) => {
    setSaving(o.id);
    const patch = on ? { dispatch_checked_at: new Date().toISOString(), dispatch_checked_by: userEmail || null } : { dispatch_checked_at: null, dispatch_checked_by: null };
    setOrders(os => os.map(x => x.id === o.id ? { ...x, ...patch } : x)); // optimista
    const { error } = await supabase.from('orders').update(patch).eq('id', o.id);
    if (error) setOrders(os => os.map(x => x.id === o.id ? { ...x, dispatch_checked_at: o.dispatch_checked_at, dispatch_checked_by: o.dispatch_checked_by } : x));
    else logActivity(userEmail, on ? 'CONTROL_DESPACHO_OK' : 'CONTROL_DESPACHO_DESTILDAR', { order_id: o.id, note: on ? 'Revisado antes del retiro de Cabify: productos y etiquetas OK' : 'Se quitó el tilde de revisado' });
    setSaving(null);
  };

  // ── Etiquetas que faltan (28/10/2026). Mismo flujo que la card: verificar dirección con Google
  //    (ensureOrderCoords) → create_batch (crea TODOS los bultos) o add_parcel (completa los que
  //    faltan) → PDF. Crear la etiqueta NO despacha ni cobra. ──
  const openLabels = async (ids: string[]) => {
    const { data, error } = await supabase.functions.invoke('cabify-logistics', { body: { action: 'labels', parcel_ids: ids } });
    if (error || !data?.ok) throw new Error(data?.error || error?.message || 'No se pudo bajar el PDF de etiquetas');
    const bytes = atob(data.pdf_base64); const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
    if (!window.open(url, '_blank')) { // popup bloqueado → descarga
      const a = document.createElement('a'); a.href = url; a.download = `etiquetas-cabify-${Date.now()}.pdf`; a.click();
    }
  };
  // Devuelve los parcel_id NUEVOS de un pedido (o tira error con el motivo).
  const makeMissing = async (o: DOrder, have: Label[], expected: number): Promise<string[]> => {
    const display = orderDisplayId(o).replace(/^#/, '');
    if (!have.length) {
      const geo = await ensureOrderCoords(o as any);
      if (!geo.ok) throw new Error(`revisá la dirección (${geo.reason}${geo.found ? ` — Google entendió: ${geo.found}` : ''}); corregila desde la card`);
      const { data, error } = await supabase.functions.invoke('cabify-logistics', { body: {
        action: 'create_batch', created_by: userEmail,
        orders: [{ order_id: o.id, order_display: display, dropoff_addr: o.delivery_address, dropoff_lat: geo.lat, dropoff_lon: geo.lon,
          dropoff_name: o.contact_name, dropoff_phone: o.contact_phone || o.canje_phone, instr: o.delivery_unit ? `Piso/depto: ${o.delivery_unit}` : undefined }],
      } });
      const res = data?.results?.[0];
      if (res && !res.ok && Array.isArray(res.existing) && res.existing.length) return res.existing;
      if (error || !data?.ok || !res?.ok) throw new Error(res?.error || data?.error || error?.message || 'no se pudo generar');
      return res.parcel_ids || [res.parcel_id];
    }
    const ids: string[] = [];
    for (let k = have.length; k < expected; k++) {
      const { data, error } = await supabase.functions.invoke('cabify-logistics', { body: { action: 'add_parcel', order_id: o.id, order_display: display, created_by: userEmail } });
      if (error || !data?.ok) throw new Error(data?.error || error?.message || 'no se pudo agregar la etiqueta');
      ids.push(data.parcel_id);
    }
    return ids;
  };
  const generate = async (targets: { o: DOrder; ls: Label[]; expected: number }[]) => {
    setBusy(targets.length === 1 ? targets[0].o.id : 'all'); setMsg(null);
    const ids: string[] = []; const fails: string[] = [];
    for (const t of targets) {
      try {
        const nuevos = await makeMissing(t.o, t.ls, t.expected);
        ids.push(...nuevos);
        logActivity(userEmail, 'ETIQUETA_CABIFY_GENERADA', { order_id: t.o.id, note: `Etiqueta(s) Cabify generada(s) desde Control de despacho: ${nuevos.length} (${t.ls.length ? 'completa las que faltaban' : 'pedido sin etiqueta'})` });
      } catch (e: any) { fails.push(`${orderDisplayId(t.o)}: ${e?.message || e}`); }
    }
    try {
      if (ids.length) await openLabels(ids);
      setMsg(fails.length
        ? { tone: 'danger', text: `${ids.length ? `Se generaron ${ids.length} etiqueta(s). ` : ''}No se pudo con: ${fails.join(' · ')}` }
        : { tone: 'success', text: `${ids.length} etiqueta(s) generada(s): se abrió el PDF para imprimir. Pegalas y tildá cada pedido.` });
    } catch (e: any) { setMsg({ tone: 'danger', text: `Se generaron, pero no se pudo abrir el PDF: ${e?.message || e}. Imprimilas desde Envíos Cabify.` }); }
    setBusy(null);
    await load();
  };

  // Lista en PDF para chequear en papel (cuadrado vacío para tildar a mano).
  const printList = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const fecha = new Date().toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.text('Control de despacho Cabify', 12, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(110);
    doc.text(`${fecha} · ${shown.length} pedidos · ${shown.reduce((s, r) => s + r.ls.length, 0)} etiquetas`, 12, 21);
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 26,
      head: [['', 'Pedido', 'Cliente', 'Etiq.', 'Productos', 'Dirección']],
      body: shown.map(r => [
        '',
        `#${orderDisplayId(r.o).replace(/^#/, '')}`,
        r.o.contact_name || '',
        `${r.ls.length}${r.expected > r.ls.length ? ` / ${r.expected}` : ''}`,
        // Helvetica del PDF es Latin-1: ⌀ salía como '#'
        (r.o.products || []).map(it => `${Number(it.quantity) || 1} × ${itemName(it).replace(/⌀/g, 'ø').replace(/[^\x20-\xff\n×–]/g, '')}`).join('\n'),
        shortAddr(r.o),
      ]),
      styles: { fontSize: 8.5, cellPadding: 1.8, valign: 'middle', lineColor: 200, lineWidth: 0.1 },
      headStyles: { fillColor: [40, 40, 40], textColor: 255 },
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 20, fontStyle: 'bold' }, 2: { cellWidth: 32 }, 3: { cellWidth: 12, halign: 'center' }, 4: { cellWidth: 72 }, 5: { cellWidth: 42 } },
      didParseCell: (d) => { if (d.section === 'body' && d.column.index === 3 && shown[d.row.index]?.problem) { d.cell.styles.textColor = [200, 30, 30]; d.cell.styles.fontStyle = 'bold'; } },
      didDrawCell: (d) => {
        if (d.section === 'body' && d.column.index === 0) {
          const s = 4.2; doc.setDrawColor(60); doc.setLineWidth(0.3);
          doc.rect(d.cell.x + (d.cell.width - s) / 2, d.cell.y + (d.cell.height - s) / 2, s, s);
        }
      },
    });
    doc.save(`control-despacho-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="tw w-full min-w-0 pb-12 pt-1">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Control de despacho</h1>
          <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
            Pedidos empaquetados que salen por Cabify. Antes de que pase el repartidor, revisá cada uno: productos y cantidad de etiquetas pegadas. Tildalo cuando esté OK.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} disabled={loading}><RefreshCw size={16} /> Actualizar</Button>
          {nProblems > 0 && (
            <Button variant="secondary" loading={busy === 'all'} disabled={busy !== null}
              onClick={() => { if (window.confirm(`¿Generar las etiquetas que faltan de ${nProblems} pedido(s) y abrir un PDF con todas para imprimir? (No despacha ni cobra.)`)) generate(rows.filter(r => r.problem)); }}>
              <Tag size={16} /> Generar etiquetas faltantes ({nProblems})
            </Button>
          )}
          <Button onClick={printList} disabled={!shown.length}><Printer size={16} /> Imprimir lista ({shown.length})</Button>
        </div>
      </header>

      {msg && (
        <div role={msg.tone === 'danger' ? 'alert' : 'status'} className={cn('mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border px-3 py-2 text-[length:var(--text-sm)]',
          msg.tone === 'danger' ? 'border-[color-mix(in_oklab,var(--danger)_30%,var(--border))] bg-[color-mix(in_oklab,var(--danger)_8%,var(--card))] text-[var(--danger)]'
            : 'border-[color-mix(in_oklab,var(--success)_30%,var(--border))] bg-[color-mix(in_oklab,var(--success)_8%,var(--card))] text-[var(--success)]')}>
          {msg.tone === 'danger' ? <AlertTriangle size={16} className="shrink-0" /> : <CheckCircle2 size={16} className="shrink-0" />}
          <span className="min-w-0 flex-1">{msg.text}</span>
          <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={() => setMsg(null)}><X size={14} /></Button>
        </div>
      )}

      <section className="mb-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <MetricCard label="Pedidos a despachar" value={rows.length} locale="es-AR" context="Empaquetados que van por Cabify" />
        <MetricCard label="Etiquetas" value={nLabels} locale="es-AR" context="Bultos que se lleva Cabify" />
        <MetricCard label="Revisados" value={nChecked} suffix={` / ${rows.length}`} locale="es-AR" context={rows.length && nChecked === rows.length ? 'Todo revisado ✓' : 'Tildados como OK'} />
        <MetricCard label="Con problema" value={nProblems} locale="es-AR" context="Sin etiqueta o con menos etiquetas que bultos" />
      </section>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl label="Ver" value={view} onValueChange={(v: any) => setView(v)}
          options={[{ value: 'pendientes', label: `Por revisar (${rows.length - nChecked})` }, { value: 'chequeados', label: `Revisados (${nChecked})` }, { value: 'todos', label: `Todos (${rows.length})` }]} />
      </div>

      {loading ? (
        <div className="grid gap-2">{[0, 1, 2, 3].map(i => <div key={i} className="h-24 animate-pulse rounded-[var(--radius-panel)] bg-[color:var(--border)] opacity-40" />)}</div>
      ) : !shown.length ? (
        <EmptyState title={rows.length ? (view === 'pendientes' ? 'Todo revisado' : 'Nada por acá') : 'No hay pedidos para despachar'}
          description={rows.length ? (view === 'pendientes' ? 'Todos los pedidos empaquetados de Cabify están tildados como OK.' : 'Cambiá el filtro para ver los demás.') : 'Cuando un pedido de Cabify pase a Empaquetado aparece acá.'} />
      ) : (
        <div className="grid gap-2">
          {shown.map(({ o, ls, expected, problem, checked }) => (
            <article key={o.id} className={cn('flex flex-wrap items-start gap-4 rounded-[var(--radius-panel)] border bg-card p-4',
              problem ? 'border-[color-mix(in_oklab,var(--danger)_45%,var(--border))]' : checked ? 'border-[color-mix(in_oklab,var(--success)_40%,var(--border))]' : 'border-line')}>
              <div className="pt-1">
                <Checkbox checked={checked} disabled={saving === o.id} aria-label={`Revisado ${orderDisplayId(o)}`} onCheckedChange={v => toggle(o, v === true)} />
              </div>
              <div className="w-40 min-w-0 shrink-0">
                <div className="text-[length:var(--text-lg)] font-semibold tabular-nums text-ink">{orderDisplayId(o)}</div>
                <div className="truncate text-[length:var(--text-sm)] text-soft">{o.contact_name}</div>
                <div className="mt-0.5 text-[length:var(--text-xs)] text-faint">{shortAddr(o)}</div>
              </div>
              <div className="w-44 shrink-0">
                <div className="flex items-center gap-1.5 text-[length:var(--text-sm)] font-semibold text-ink">
                  <Tag size={14} className="text-soft" /> {ls.length} etiqueta{ls.length === 1 ? '' : 's'}
                  {expected > 1 && <span className="font-normal text-soft">· {expected} bultos</span>}
                </div>
                {ls.length > 0 && (
                  <div className="mt-1 grid gap-0.5 text-[length:var(--text-xs)] text-soft">
                    {ls.map((l, i) => <span key={i} className="tabular-nums">{l.external_id || '—'} · {CABIFY_STATE_ES[l.status] ? CABIFY_STATE_ES[l.status].split(' (')[0].split(' —')[0] : l.status}</span>)}
                  </div>
                )}
                {!problem && ls.length > 0 && (
                  <Button variant="ghost" size="sm" className="mt-1" disabled={busy !== null} title="Otra etiqueta igual: el pedido va en más de una caja"
                    onClick={() => { if (window.confirm(`¿Agregar otro bulto a ${orderDisplayId(o)}? Se genera una etiqueta Cabify más a la misma dirección.`)) generate([{ o, ls, expected: ls.length + 1 }]); }}>
                    {busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <Tag size={13} />} + Bulto
                  </Button>
                )}
                {problem && (
                  <div className="mt-1.5 grid justify-items-start gap-1.5">
                    <Badge tone="danger" size="sm" icon={<AlertTriangle size={12} />}>{problem}</Badge>
                    <Button size="sm" disabled={busy !== null} onClick={() => generate([{ o, ls, expected }])}>
                      {busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
                      {ls.length ? `Generar ${expected - ls.length} faltante${expected - ls.length > 1 ? 's' : ''} e imprimir` : `Generar ${expected > 1 ? `${expected} etiquetas` : 'etiqueta'} e imprimir`}
                    </Button>
                  </div>
                )}
              </div>
              <ul className="m-0 grid min-w-[16rem] flex-1 list-none gap-1.5 p-0">
                {(o.products || []).map((it, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <Thumb src={images.get(pidOf(it))} name={itemName(it)} size="sm" />
                    <span className="min-w-0 text-[length:var(--text-sm)] text-ink">
                      <b className="tabular-nums">{Number(it.quantity) || 1} ×</b> {itemName(it)}
                    </span>
                  </li>
                ))}
              </ul>
              {checked && (
                <div className="flex w-full items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--success)] sm:w-auto sm:flex-col sm:items-end">
                  <CheckCircle2 size={14} /> Revisado {o.dispatch_checked_at ? new Date(o.dispatch_checked_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : ''}
                  {o.dispatch_checked_by && <span className="text-faint">{o.dispatch_checked_by.split('@')[0]}</span>}
                  <Button variant="ghost" size="sm" onClick={() => toggle(o, false)} title="Quitar el tilde"><Undo2 size={13} /></Button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
